import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import "@/App.css";

// Pages
import Home from "./pages/Home";
import Browse from "./pages/Browse";
import ProfilePage from "./pages/ProfilePage";
import JobBoard from "./pages/JobBoard";
import JobDetail from "./pages/JobDetail";
import PostJob from "./pages/PostJob";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdvertiserDashboard from "./pages/AdvertiserDashboard";
import Categories from "./pages/Categories";
import Locations from "./pages/Locations";
import Pricing from "./pages/Pricing";
import Contact from "./pages/Contact";
import About from "./pages/About";
import Checkout from "./pages/Checkout";
import PaymentResult from "./pages/PaymentResult";
import ErrorBoundary from "./components/ErrorBoundary";
import { RouteSEO } from "./components/RouteSEO";
import DirectoryPage from "./pages/DirectoryPage";
import Guides from "./pages/Guides";
import NotFound from "./pages/NotFound";
import { Terms, Privacy, Cookies, Refund } from "./pages/Legal";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
import { createContext, useContext } from "react";

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Callback Component - handles OAuth redirect
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          // Clear the hash and redirect to dashboard with user data
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard', { state: { user: response.data }, replace: true });
        } catch (error) {
          console.error("Auth error:", error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    processSession();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-zinc-400">Authenticating...</p>
      </div>
    </div>
  );
};

const RegisterRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/signup${search}`} replace />;
};

// App Router with auth detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id DURING RENDER (not in useEffect)
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <RouteSEO />
      <Navbar />
      <main className="min-h-screen">
        <ErrorBoundary resetKey={location.pathname}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/profile/:profileId" element={<ProfilePage />} />
          <Route path="/jobs" element={<JobBoard />} />
          <Route path="/jobs/:jobId" element={<JobDetail />} />
          <Route path="/post-job" element={<PostJob />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Register />} />
          <Route path="/register" element={<RegisterRedirect />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/advertiser" element={<AdvertiserDashboard />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/categories/:categorySlug" element={<DirectoryPage />} />
          <Route path="/categories/:categorySlug/:locationSlug" element={<DirectoryPage />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/locations/:locationSlug" element={<DirectoryPage />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/advertise" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/refund-policy" element={<Refund />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/payment/result" element={<PaymentResult />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </ErrorBoundary>
      </main>
      <Footer />
    </>
  );
}

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      if (window.caches) await caches.delete("vct-api-v2");
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App film-grain">
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" theme="dark" />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
